import React, { useEffect, useRef, useState } from "react";

import { popupService } from "api/services/marketing.service";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";

import type { PopupItem, PopupLinkType, PopupPosition } from "api/types/marketing.types";

interface PopupFormState {
  title: string;
  image: File | null;
  alt_text: string;
  link_type: PopupLinkType;
  link_url: string;
  position: PopupPosition;
  priority: number;
  start_date: string;
  end_date: string;
}

const INITIAL_FORM: PopupFormState = {
  title: "",
  image: null,
  alt_text: "",
  link_type: "NONE",
  link_url: "",
  position: "CENTER",
  priority: 0,
  start_date: "",
  end_date: "",
};

const LINK_TYPE_OPTIONS = [
  { value: "NONE", label: "없음" },
  { value: "INTERNAL", label: "내부 링크" },
  { value: "EXTERNAL", label: "외부 링크" },
];

const POSITION_OPTIONS = [
  { value: "CENTER", label: "중앙" },
  { value: "TOP", label: "상단" },
  { value: "BOTTOM", label: "하단" },
];

interface CreatePopUpModalProps {
  open: boolean;
  editTarget: PopupItem | null;
  onClose: () => void;
  onSuccess: () => void;
}

const CreatePopUpModal: React.FC<CreatePopUpModalProps> = ({ open, editTarget, onClose, onSuccess }) => {
  const [form, setForm] = useState<PopupFormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (editTarget) {
      setForm({
        title: editTarget.title,
        image: null,
        alt_text: editTarget.alt_text,
        link_type: editTarget.link_type,
        link_url: editTarget.link_url,
        position: editTarget.position,
        priority: editTarget.priority,
        start_date: editTarget.start_date,
        end_date: editTarget.end_date,
      });
    } else {
      setForm(INITIAL_FORM);
    }
  }, [open, editTarget]);

  const updateField = <K extends keyof PopupFormState>(key: K, value: PopupFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const buildFormData = (): FormData => {
    const fd = new FormData();
    fd.append("title", form.title);
    if (form.image) fd.append("image", form.image);
    fd.append("alt_text", form.alt_text);
    fd.append("link_type", form.link_type);
    fd.append("link_url", form.link_url);
    fd.append("position", form.position);
    fd.append("priority", String(form.priority));
    fd.append("start_date", form.start_date);
    fd.append("end_date", form.end_date);
    return fd;
  };

  const handleSubmit = async () => {
    if (!form.title || !form.start_date || !form.end_date) {
      alert("제목, 시작일, 종료일은 필수입니다.");
      return;
    }
    if (!editTarget && !form.image) {
      alert("이미지를 선택해주세요.");
      return;
    }

    setSubmitting(true);
    try {
      const fd = buildFormData();
      if (editTarget) {
        await popupService.update(editTarget.id, fd);
      } else {
        await popupService.create(fd);
      }
      onSuccess();
    } catch {
      alert("저장에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={editTarget ? "팝업 수정" : "팝업 생성"}
      onClose={onClose}
      width="520px"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "저장 중..." : editTarget ? "수정" : "생성"}
          </Button>
        </>
      }
    >
      <div className="popup-management__form">
        <Input label="제목" placeholder="팝업 제목 입력" value={form.title} onChange={(e) => updateField("title", e.target.value)} />

        <div className="popup-management__form-field">
          <label className="input-label">팝업 이미지</label>
          <input ref={fileInputRef} type="file" accept="image/*" className="popup-management__file-input" onChange={(e) => updateField("image", e.target.files?.[0] ?? null)} />
          {editTarget?.image_url && !form.image && <img className="popup-management__preview" src={editTarget.image_url} alt="현재 이미지" />}
          {form.image && <p className="popup-management__file-name">{form.image.name}</p>}
        </div>

        <Input label="대체 텍스트" placeholder="이미지 대체 텍스트" value={form.alt_text} onChange={(e) => updateField("alt_text", e.target.value)} />

        <div className="popup-management__form-row">
          <Select label="링크 유형" options={LINK_TYPE_OPTIONS} value={form.link_type} onChange={(e) => updateField("link_type", e.target.value as PopupLinkType)} />
          <Select label="위치" options={POSITION_OPTIONS} value={form.position} onChange={(e) => updateField("position", e.target.value as PopupPosition)} />
        </div>

        {form.link_type !== "NONE" && <Input label="링크 URL" placeholder="https://..." value={form.link_url} onChange={(e) => updateField("link_url", e.target.value)} />}

        <Input label="우선순위" type="number" placeholder="0" value={String(form.priority)} onChange={(e) => updateField("priority", Number(e.target.value))} />

        <div className="popup-management__form-row">
          <Input label="시작일" type="date" value={form.start_date} onChange={(e) => updateField("start_date", e.target.value)} />
          <Input label="종료일" type="date" value={form.end_date} onChange={(e) => updateField("end_date", e.target.value)} />
        </div>
      </div>
    </Modal>
  );
};

export default CreatePopUpModal;
